import React, { useEffect, useState } from 'react';
import { FONTS } from '../../styles/tokens';
import { SecretRow } from './SecretRow';
import { scanEnvVars, type ScannedEnvVar } from '../../lib/deploy-api';

interface Colors {
  text: string;
  textSecondary: string;
  card: string;
  border: string;
}

interface SecretCaptureStepProps {
  token: string;
  zip: File;
  colors: Colors;
  /** Invoked with the captured values when the user confirms. */
  onContinue: (secrets: Array<{ key: string; value: string; isSecret: boolean }>) => void;
  onBack: () => void;
}

interface RowState {
  entry: ScannedEnvVar;
  value: string;
  isSecret: boolean;
  skipped: boolean;
}

/**
 * Step between Upload and Review. Scans the uploaded zip for env-var
 * references, asks the AI classifier what's sensitive, and renders a per-var
 * form. The user confirms values (or skips) before the actual deploy.
 */
export function SecretCaptureStep(props: SecretCaptureStepProps): React.JSX.Element {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    scanEnvVars(props.token, props.zip)
      .then(result => {
        if (cancelled) return;
        if (!result) {
          setError('Scan failed — continuing without secret capture.');
          setRows([]);
          return;
        }
        setRows(result.items.map(entry => ({
          entry,
          value: entry.suggestedDefault ?? '',
          isSecret: entry.isSecret,
          skipped: false,
        })));
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Scan failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [props.token, props.zip]);

  const update = (name: string, patch: Partial<RowState>): void =>
    setRows(prev => prev.map(r => r.entry.name === name ? { ...r, ...patch } : r));

  const submit = (): void => {
    const collected = rows
      .filter(r => !r.skipped && r.value.length > 0)
      .map(r => ({ key: r.entry.name, value: r.value, isSecret: r.isSecret }));
    props.onContinue(collected);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <header>
        <h3 style={{ fontFamily: FONTS.heading, fontSize: '13px', letterSpacing: '0.2em', textTransform: 'uppercase', color: props.colors.text, margin: 0 }}>
          Review detected environment variables
        </h3>
        <p style={{ fontFamily: FONTS.body, fontSize: '12px', color: props.colors.textSecondary, margin: '6px 0 0' }}>
          We scanned your zip for references to environment variables. Provide values for anything your app needs at runtime. Skip anything you'd rather configure post-deploy.
        </p>
      </header>

      {loading && (
        <EmptyMessage text="Scanning source for env-var references…" colors={props.colors} />
      )}

      {!loading && error && (
        <EmptyMessage text={error} colors={props.colors} />
      )}

      {!loading && !error && rows.length === 0 && (
        <EmptyMessage text="No environment variables detected — you can still set them after deploy." colors={props.colors} />
      )}

      {!loading && rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {rows.map(row => (
            <SecretRow
              key={row.entry.name}
              entry={{ ...row.entry, isSecret: row.isSecret }}
              value={row.value}
              onChangeValue={v => update(row.entry.name, { value: v })}
              onToggleSecret={next => update(row.entry.name, { isSecret: next })}
              onSkip={() => update(row.entry.name, { skipped: !row.skipped })}
              skipped={row.skipped}
              colors={props.colors}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
        <button
          type="button"
          onClick={props.onBack}
          style={{ padding: '8px 14px', borderRadius: '6px', border: `1px solid ${props.colors.border}`, background: 'transparent', color: props.colors.textSecondary, cursor: 'pointer', fontFamily: FONTS.body, fontSize: '12px' }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={loading}
          style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: loading ? '#666' : '#01A9ED', color: '#FFF', cursor: loading ? 'default' : 'pointer', fontFamily: FONTS.heading, fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase' }}
        >
          Continue to review
        </button>
      </div>
    </div>
  );
}

/** Inline empty-state / loading placeholder. */
function EmptyMessage(props: { text: string; colors: Colors }): React.JSX.Element {
  return (
    <div style={{ padding: '14px', borderRadius: '8px', border: `1px dashed ${props.colors.border}`, color: props.colors.textSecondary, fontFamily: FONTS.body, fontSize: '12px' }}>
      {props.text}
    </div>
  );
}
